/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AfterViewInit, ChangeDetectorRef, Component, Input } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup } from '@angular/forms';
import { CountryCodes } from '../../enums';

@Component({
    selector: 'app-phone-number',
    templateUrl: './phone-number.component.html',
    styleUrls: ['./phone-number.component.scss']
})
export class PhoneNumberComponent implements AfterViewInit, ControlValueAccessor {
    /**
     * Our form group
     */
    @Input() set formGroup(formGroup: FormGroup) {
        this._formGroup = formGroup;
        this.setUpFormSubscription();
    }

    /**
     * Default country in dropdown.
     */
    @Input() set defaultCountryCode(countryCode: number) {
        this._defaultCountryCode = countryCode;
        if (this.formGroup) {
            this.formGroup.get('countryCode').setValue(countryCode);
        }
    }

    /**
     * Name of the field in the form
     */
    @Input() public numberFormName: string = 'phoneNumber';

    /**
     * Apply external styling
     */
    @Input() public applyExternalStyling: boolean = false;

    /**
     * Apply external styling
     */
    @Input() public title: string = 'contact_number';

    /**
     * Dropdown content.
     */
    public dropdown: any = {
        countryCodes: CountryCodes
    };

    /**
     * Loaded state as input come in later.
     */
    public loaded: boolean = false;

    /**
     * Store the selected country code.
     */
    public selectedCountryCode: string;

    /**
     * Class variable default country code.
     * @private
     */
    private _defaultCountryCode: number;

    /**
     * Class variable for internal form group.
     * @private
     */
    private _formGroup: FormGroup = new FormGroup({ phoneNumber: new FormControl('') });

    constructor(
        private cdr: ChangeDetectorRef
    ) {
    }

    get formGroup(): FormGroup {
        return this._formGroup;
    }

    get defaultCountryCode(): number {
        return this._defaultCountryCode;
    }

    ngAfterViewInit(): void {
        this.loaded = true;
        if (!this.defaultCountryCode) {
            this.setDefaultCountryCode();
        }

        this.cdr.detectChanges();
    }

    /**
     * Dropdown with set by value length.
     */
    public dropdownWidth(): number {
        if (this.formGroup) {
            const value: any = this.formGroup.get('countryCode')?.value;
            let width: number;
            switch (value?.length) {
                case 1:
                case 2:
                    width = 80;
                    break;
                case 3:
                    width = 88;
                    break;
                case 4:
                    width = 95;
                    break;
                case 5:
                    width = 100;
                    break;
                default:
                    width = 100;
            }

            return this.applyExternalStyling ? width - 30 : width;
        }
    }

    /**
     * Set default country code.
     * @private
     */
    private setDefaultCountryCode(): void {
        const countryLocale: string = Intl.DateTimeFormat().resolvedOptions().locale;
        const countryIndex: number = this.getCountryIndex(countryLocale);
        if (this.formGroup.get('countryCode') && countryIndex !== -1) {
            const preSelectedItem: any = this.dropdown.countryCodes[countryIndex];
            this.formGroup.get('countryCode').setValue(preSelectedItem.key);
        }
    }

    /**
     * Get country index based of the browser locale.
     * @param countryLocale
     * @private
     */
    private getCountryIndex(countryLocale: string): number {
        const localeParts: string[] = countryLocale.split('-');
        const countryCode: string = localeParts[localeParts?.length - 1];
        if (countryCode) {
            return this.dropdown.countryCodes.findIndex((item: any) => this.matchIsoCode(item.options.isoCode, countryCode));
        }
    }

    /**
     * Match ISO
     * @param isoCode
     * @param countryCode
     * @private
     */
    private matchIsoCode(isoCode: string, countryCode: string): boolean {
        const isoParts: string[] = isoCode.split('/');

        return isoParts[0] === countryCode;
    }

    /**
     * Set up subscription.
     * @private
     */
    private setUpFormSubscription(): void {
        this.formGroup.valueChanges.subscribe((values: any) => {
            const phoneNumber: any = values[this.numberFormName];
            this.selectedCountryCode = this.dropdown.countryCodes.find(
                (item: { key: any; }): boolean => item.key === values.countryCode
            ).value;
            if (typeof phoneNumber === 'number') {
                this.formGroup.get(this.numberFormName).setValue(phoneNumber.toString());
            }
        });
    }

    /**
     * Event handler for when the view changes
     * // Note: Required by implementation. Do not remove.
     */
    public registerOnChange(): void {
    }

    /**
     * Register a function to determine if the input has been touched
     * // Note: Required by implementation. Do not remove.
     */
    public registerOnTouched(): void {
    }

    /**
     * Write value to input
     * // Note: Required by implementation. Do not remove.
     */
    public writeValue(): void {
    }
}
